import { DEFAULT_PANE_ID, DEFAULT_PANE_ID_PREFIX } from '../constants';
import type {
  ChartV2Point,
  ChartV2Series,
  ChartV2SeriesType,
} from '../types/chart-v2.types';

export interface InternalSeriesMeta {
  color?: string;
  seriesType?: ChartV2SeriesType;
  paneIndex: number;
}

export interface LivePerSeries {
  points: ChartV2Point[];
}

export interface Pane<T extends ChartV2Series = ChartV2Series> {
  id: string;
  series: T[];
}

export const parsePaneIndex = (id: string): number => {
  if (!id.startsWith(DEFAULT_PANE_ID_PREFIX)) {
    return Number.POSITIVE_INFINITY;
  }

  const n = Number(id.slice(DEFAULT_PANE_ID_PREFIX.length));

  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

export const groupByPane = <T extends ChartV2Series>(
  series: T[]
): Pane<T>[] => {
  const map = new Map<string, T[]>();
  const firstSeen = new Map<string, number>();

  series.forEach((s, i) => {
    const paneId = s.paneId ?? DEFAULT_PANE_ID;
    const bucket = map.get(paneId);

    if (bucket) {
      bucket.push(s);
      return;
    }

    map.set(paneId, [s]);
    firstSeen.set(paneId, i);
  });

  // Primary ordering is by the numeric index embedded in the pane id so the
  // `Move up` / `Move down` buttons produce an immediate visual change.
  // Custom pane ids fall through to insertion order.
  const order = Array.from(map.keys()).sort((a, b) => {
    const ai = parsePaneIndex(a);
    const bi = parsePaneIndex(b);

    if (ai !== bi) {
      return ai - bi;
    }

    return (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0);
  });

  return order.map(id => ({ id, series: map.get(id) ?? [] }));
};

export const compactPaneIndices = (
  meta: Record<string, InternalSeriesMeta>
): Record<string, InternalSeriesMeta> => {
  const used = new Set(Object.values(meta).map(m => m.paneIndex));
  const sorted = Array.from(used).sort((a, b) => a - b);
  const remap = new Map<number, number>();

  sorted.forEach((idx, i) => remap.set(idx, i));

  const next: Record<string, InternalSeriesMeta> = {};

  for (const id of Object.keys(meta)) {
    const current = meta[id];

    next[id] = {
      ...current,
      paneIndex: remap.get(current.paneIndex) ?? 0,
    };
  }

  return next;
};

export const seriesPaneId = (series: ChartV2Series): string =>
  series.paneId ?? DEFAULT_PANE_ID;
